import type { Book, ReadingStatus, StarRating } from "../models/types";
import {
  sortBooksBy,
  filterBooks,
  createNewBook,
  createEmptyLibrary,
} from "../models/library";
import * as storage from "../services/storage";
import { saveSortOption } from "../services/local-storage";
import {
  searchBooks,
  searchByIsbn,
  type OpenLibraryResult,
} from "../services/openlibrary";
import { importGoodreadsFile } from "../services/goodreads-import";
import { runCoverScan } from "./components/cover-scanner";
import type { AppState, SortOption } from "./state";
import { createInitialState, getSelectedBook } from "./state";
import {
  renderHeader,
  renderNavBar,
  renderMobileSearchBar,
  renderFilterModal,
  renderUtilitiesMenu,
  renderSortModal,
} from "./components/layout";
import { renderBookList } from "./components/book-list";
import { renderBookForm } from "./components/book-form";
import { renderConfirmModal } from "./components/modal";
import {
  Autocomplete,
} from "./components/autocomplete";
import { renderCoverImage } from "./components/cover-image";
import { bindDateChipEvents, updateChipToPickState } from "./components/date-chip-selector";
import { renderCalendar, bindCalendarEvents } from "./components/lcars-calendar";
import * as router from "./router";
import { performSave } from "./components/saving-overlay";

let state: AppState;
let appEl: HTMLElement;
let activeAutocompletes: Autocomplete[] = [];
let _discardResolve: ((value: boolean) => void) | null = null;

const SS_AUTH_REDIRECT_PATH = "booklist_auth_redirect_path";

/** Initialize the app */
export async function initApp(rootEl: HTMLElement): Promise<void> {
  appEl = rootEl;

  // If not signed in but we have a refresh token, try to silently refresh
  if (!storage.isSignedIn() && storage.hasRefreshToken()) {
    showLoading();
    const refreshed = await storage.trySilentRefresh();
    if (!refreshed) {
      saveDeepLinkPath();
      renderAuthRequired();
      return;
    }
  }

  if (!storage.isSignedIn()) {
    saveDeepLinkPath();
    renderAuthRequired();
    return;
  }

  showLoading();

  try {
    const library = await storage.load();
    state = createInitialState(library);
  } catch (err) {
    console.error("Failed to load library:", err);
    state = createInitialState(createEmptyLibrary());
    showToast("Failed to load data. Starting with empty library.");
  }

  // Restore deep-link path from auth redirect if available
  const savedPath = sessionStorage.getItem(SS_AUTH_REDIRECT_PATH);
  if (savedPath) {
    sessionStorage.removeItem(SS_AUTH_REDIRECT_PATH);
  }

  router.init(
    {
      state,
      render: () => render(),
      isDirty: () => {
        const form = appEl.querySelector<HTMLFormElement>(".book-form");
        if (!form || state.formSnapshot === null) return false;
        return serializeForm(form) !== state.formSnapshot;
      },
      showDiscardModal: () =>
        new Promise<boolean>((resolve) => {
          _discardResolve = resolve;
          state.modal = {
            title: "Discard Changes",
            message: "You have unsaved changes. Discard them?",
            confirmLabel: "Discard",
            confirmColor: "orange",
            onConfirm: () => {
              _discardResolve = null;
              resolve(true);
            },
          };
          // Inject modal directly into DOM to avoid full re-render losing form edits
          const modalHtml = renderConfirmModal(state.modal);
          appEl.insertAdjacentHTML("beforeend", modalHtml);
          const overlay = appEl.querySelector("#modal-overlay");
          overlay
            ?.querySelector("#modal-confirm-btn")
            ?.addEventListener("click", () => {
              _discardResolve = null;
              state.modal = null;
              overlay.remove();
              resolve(true);
            });
          overlay
            ?.querySelector("#modal-cancel-btn")
            ?.addEventListener("click", () => {
              _discardResolve = null;
              state.modal = null;
              overlay.remove();
              resolve(false);
            });
          overlay?.addEventListener("click", (e) => {
            if ((e.target as HTMLElement).id === "modal-overlay") {
              _discardResolve = null;
              state.modal = null;
              overlay.remove();
              resolve(false);
            }
          });
        }),
    },
    savedPath ?? undefined,
  );

  render();
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

function render(): void {
  destroyAutocompletes();
  const content = getContentHtml();

  appEl.innerHTML = `
    <div class="lcars-app">
      ${renderHeader(state)}
      <div class="lcars-body">
        ${renderMobileSearchBar(state)}
        ${renderNavBar(state)}
        <main class="lcars-content">
          ${content}
        </main>
      </div>
    </div>
    ${state.showUtilities ? renderUtilitiesMenu() : ""}
    ${state.modal ? renderConfirmModal(state.modal) : ""}
    ${state.toast ? `<div class="lcars-toast">${state.toast}</div>` : ""}
  `;

  bindEvents();

  if (state.view === "list" && state.savedScrollTop > 0) {
    const contentEl = appEl.querySelector<HTMLElement>(".lcars-content");
    if (contentEl) {
      contentEl.scrollTop = state.savedScrollTop;
    }
  }
}

function getContentHtml(): string {
  if (state.loading) {
    return '<div class="empty-state"><div class="empty-state__title">Loading...</div></div>';
  }

  if (state.view === "notFound") {
    return `
      <div class="not-found">
        <div class="not-found__title">RECORD NOT FOUND</div>
        <div class="not-found__subtitle">Database Query Returned No Results</div>
        <div class="not-found__text">
          The requested library entry could not be located in the computer core database.
        </div>
        <button class="lcars-btn lcars-btn--blue" id="not-found-return-btn">Return to Library</button>
      </div>
    `;
  }

  if (state.view === "add") {
    const newBook = createNewBook();
    return renderBookForm(newBook, true);
  }

  if (state.view === "detail") {
    const book = getSelectedBook(state);
    if (book) return renderBookForm(book, false);
    state.view = "notFound";
    return getContentHtml();
  }

  // List view
  let books = state.library.books;
  if (state.filterStatus) {
    books = books.filter((b) => b.status === state.filterStatus);
  }
  books = filterBooks(books, state.searchQuery);
  books = sortBooksBy(books, state.sortOption);

  return renderBookList(books);
}

function renderAuthRequired(): void {
  appEl.innerHTML = `
    <div class="lcars-app">
      <header class="lcars-header">
        <div class="lcars-header__elbow">
          <span class="lcars-header__elbow-label">LCARS ${__APP_VERSION__}</span>
        </div>
        <div class="lcars-header__bar">
          <div class="lcars-header__top-strip">
            <span></span><span></span><span></span><span></span>
          </div>
          <div class="lcars-header__title-row">
            <div class="lcars-header__title-bar">
              <h1 class="lcars-header__title">Book List</h1>
            </div>
          </div>
        </div>
      </header>
      <div class="auth-required">
        <div class="auth-required__panel">
          <div class="auth-required__indicator"></div>
          <div class="auth-required__content">
            <div class="auth-required__title">AUTHORIZATION REQUIRED</div>
            <div class="auth-required__subtitle">Security Protocol 47-Alpha</div>
            <div class="auth-required__text">
              Access to the Library Computer system requires authenticated credentials.
              Please verify your identity through the Microsoft authorization gateway to proceed.
            </div>
            <button class="lcars-btn lcars-btn--blue auth-required__btn" id="auth-sign-in-btn">
              Initiate Sign In
            </button>
          </div>
          <div class="auth-required__indicator auth-required__indicator--alt"></div>
        </div>
      </div>
    </div>
  `;

  appEl
    .querySelector("#auth-sign-in-btn")
    ?.addEventListener("click", async () => {
      try {
        await storage.signIn();
      } catch (err) {
        console.error("Sign in failed:", err);
      }
    });
}

function showLoading(): void {
  appEl.innerHTML = `
    <div class="lcars-app">
      <div style="display:flex;align-items:center;justify-content:center;height:100vh;">
        <div class="empty-state__title">Accessing Library Computer...</div>
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Event Binding
// ---------------------------------------------------------------------------

function bindEvents(): void {
  // Nav bar filter buttons
  appEl.querySelectorAll<HTMLButtonElement>("[data-filter]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const filter = btn.dataset.filter || null;
      state.filterStatus = filter as ReadingStatus | null;
      render();
      router.replaceUrl({ search: state.searchQuery, filter: state.filterStatus });
    });
  });

  // Desktop sort buttons
  appEl.querySelectorAll<HTMLButtonElement>("[data-sort]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const option = btn.dataset.sort as SortOption;
      if (option !== state.sortOption) {
        state.sortOption = option;
        saveSortOption(option);
        render();
      }
    });
  });

  // Mobile sort trigger
  appEl.querySelector("#sort-trigger-btn")?.addEventListener("click", () => {
    const modalHtml = renderSortModal(state.sortOption);
    appEl.insertAdjacentHTML("beforeend", modalHtml);
    const overlay = appEl.querySelector("#sort-modal-overlay");
    overlay?.querySelectorAll<HTMLButtonElement>("[data-sort-modal]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const option = btn.dataset.sortModal as SortOption;
        state.sortOption = option;
        saveSortOption(option);
        overlay.remove();
        render();
      });
    });
    overlay?.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).id === "sort-modal-overlay") {
        overlay.remove();
      }
    });
  });

  // Mobile filter trigger
  appEl.querySelector("#filter-trigger-btn")?.addEventListener("click", () => {
    const modalHtml = renderFilterModal(state);
    appEl.insertAdjacentHTML("beforeend", modalHtml);
    const overlay = appEl.querySelector("#filter-modal-overlay");
    overlay?.querySelectorAll<HTMLButtonElement>("[data-filter-modal]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const filter = btn.dataset.filterModal || null;
        state.filterStatus = filter as ReadingStatus | null;
        overlay.remove();
        render();
        router.replaceUrl({ search: state.searchQuery, filter: state.filterStatus });
      });
    });
    overlay?.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).id === "filter-modal-overlay") {
        overlay.remove();
      }
    });
  });

  // Add book button
  appEl.querySelector("#add-book-btn")?.addEventListener("click", () => {
    saveScrollPosition();
    state.view = "add";
    state.selectedBookId = null;
    router.navigateTo("/book/new");
    render();
  });

  // Utilities button
  appEl.querySelector("#utilities-btn")?.addEventListener("click", () => {
    state.showUtilities = true;
    render();
  });

  // Utilities menu events
  if (state.showUtilities) {
    appEl
      .querySelector("#utilities-overlay")
      ?.addEventListener("click", (e) => {
        if ((e.target as HTMLElement).id === "utilities-overlay") {
          state.showUtilities = false;
          render();
        }
      });
    appEl
      .querySelector("#utilities-close-btn")
      ?.addEventListener("click", () => {
        state.showUtilities = false;
        render();
      });
    appEl
      .querySelector("#sign-out-btn")
      ?.addEventListener("click", handleSignOut);
    appEl.querySelector("#export-btn")?.addEventListener("click", () => {
      state.showUtilities = false;
      handleExport();
    });
    appEl.querySelector("#import-btn")?.addEventListener("click", () => {
      state.showUtilities = false;
      handleImport();
    });
    appEl.querySelector("#goodreads-btn")?.addEventListener("click", () => {
      state.showUtilities = false;
      handleGoodreadsImport();
    });
    appEl.querySelector("#find-covers-btn")?.addEventListener("click", () => {
      state.showUtilities = false;
      handleFindMissingCovers();
    });
  }

  // Nav bar action buttons
  appEl.querySelector("#nav-save-btn")?.addEventListener("click", () => {
    const form = appEl.querySelector<HTMLFormElement>(".book-form");
    if (form) form.requestSubmit();
  });

  appEl.querySelector("#nav-cancel-btn")?.addEventListener("click", () => {
    const form = appEl.querySelector<HTMLFormElement>(".book-form");
    if (form && state.formSnapshot !== null && serializeForm(form) !== state.formSnapshot) {
      state.modal = {
        title: "Discard Changes",
        message: "You have unsaved changes. Discard them?",
        confirmLabel: "Discard",
        confirmColor: "orange",
        onConfirm: () => {
          state.view = "list";
          state.selectedBookId = null;
          state.formSnapshot = null;
          router.navigateTo("/");
          render();
        },
      };
      // Inject modal directly into DOM to preserve form edits
      const modalHtml = renderConfirmModal(state.modal);
      appEl.insertAdjacentHTML("beforeend", modalHtml);
      const overlay = appEl.querySelector("#modal-overlay");
      overlay
        ?.querySelector("#modal-confirm-btn")
        ?.addEventListener("click", () => {
          const onConfirm = state.modal?.onConfirm;
          state.modal = null;
          overlay.remove();
          onConfirm?.();
        });
      overlay
        ?.querySelector("#modal-cancel-btn")
        ?.addEventListener("click", () => {
          state.modal = null;
          overlay.remove();
        });
      overlay?.addEventListener("click", (e) => {
        if ((e.target as HTMLElement).id === "modal-overlay") {
          state.modal = null;
          overlay.remove();
        }
      });
    } else {
      state.view = "list";
      state.selectedBookId = null;
      state.formSnapshot = null;
      router.navigateTo("/");
      render();
    }
  });

  appEl.querySelector("#nav-delete-btn")?.addEventListener("click", () => {
    const form = appEl.querySelector<HTMLFormElement>(".book-form");
    const bookId = form?.dataset.bookId;
    if (!bookId) return;

    const book = state.library.books.find((b) => b.id === bookId);
    const title = book?.title ?? "this book";

    state.modal = {
      title: "Confirm Delete",
      message: `Delete "${title}"? This cannot be undone.`,
      confirmLabel: "Delete",
      confirmColor: "red",
      onConfirm: async () => {
        state.library.books = state.library.books.filter((b) => b.id !== bookId);
        const saved = await performSave(() => storage.save(state.library));
        if (saved) {
          state.view = "list";
          state.selectedBookId = null;
          state.formSnapshot = null;
          router.navigateTo("/");
          render();
          showToast("Book deleted");
        }
      },
    };
    render();
  });

  // Search input
  const searchInput = appEl.querySelector<HTMLInputElement>("#search-input");
  if (searchInput) {
    searchInput.addEventListener("input", () => {
      state.searchQuery = searchInput.value;
      render();
      // Re-focus the search input after render
      const newInput = appEl.querySelector<HTMLInputElement>("#search-input");
      if (newInput) {
        newInput.focus();
        newInput.selectionStart = newInput.selectionEnd = newInput.value.length;
      }
      router.replaceUrl({ search: state.searchQuery, filter: state.filterStatus });
    });
  }

  // Desktop search clear button
  const searchClearBtn = appEl.querySelector<HTMLButtonElement>("#search-clear-btn");
  if (searchClearBtn) {
    searchClearBtn.addEventListener("click", () => {
      state.searchQuery = "";
      render();
      const newInput = appEl.querySelector<HTMLInputElement>("#search-input");
      if (newInput) newInput.focus();
      router.replaceUrl({ search: state.searchQuery, filter: state.filterStatus });
    });
  }

  // Mobile search input
  const mobileSearchInput = appEl.querySelector<HTMLInputElement>("#mobile-search-input");
  if (mobileSearchInput) {
    mobileSearchInput.addEventListener("input", () => {
      state.searchQuery = mobileSearchInput.value;
      render();
      const newInput = appEl.querySelector<HTMLInputElement>("#mobile-search-input");
      if (newInput) {
        newInput.focus();
        newInput.selectionStart = newInput.selectionEnd = newInput.value.length;
      }
      router.replaceUrl({ search: state.searchQuery, filter: state.filterStatus });
    });
  }

  // Mobile search clear button
  const mobileSearchClearBtn = appEl.querySelector<HTMLButtonElement>("#mobile-search-clear-btn");
  if (mobileSearchClearBtn) {
    mobileSearchClearBtn.addEventListener("click", () => {
      state.searchQuery = "";
      render();
      const newInput = appEl.querySelector<HTMLInputElement>("#mobile-search-input");
      if (newInput) newInput.focus();
      router.replaceUrl({ search: state.searchQuery, filter: state.filterStatus });
    });
  }

  // Book card clicks → detail view
  appEl.querySelectorAll<HTMLElement>("[data-book-id]").forEach((card) => {
    // Only book cards, not forms
    if (card.tagName === "FORM") return;
    card.addEventListener("click", () => {
      saveScrollPosition();
      state.selectedBookId = card.dataset.bookId!;
      state.view = "detail";
      router.navigateTo("/book/" + card.dataset.bookId!);
      render();
    });
  });

  // Book form handling
  const form = appEl.querySelector<HTMLFormElement>(".book-form");
  if (form) {
    bindFormEvents(form);
  }

  // Not-found return button
  appEl.querySelector("#not-found-return-btn")?.addEventListener("click", () => {
    state.view = "list";
    state.selectedBookId = null;
    router.navigateTo("/");
    render();
  });

  // Modal events
  if (state.modal) {
    appEl.querySelector("#modal-confirm-btn")?.addEventListener("click", () => {
      const onConfirm = state.modal?.onConfirm;
      state.modal = null;
      render();
      onConfirm?.();
    });

    appEl.querySelector("#modal-cancel-btn")?.addEventListener("click", () => {
      if (_discardResolve) {
        const resolve = _discardResolve;
        _discardResolve = null;
        state.modal = null;
        resolve(false);
        return;
      }
      state.modal = null;
      render();
    });

    appEl.querySelector("#modal-overlay")?.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).id === "modal-overlay") {
        if (_discardResolve) {
          const resolve = _discardResolve;
          _discardResolve = null;
          state.modal = null;
          resolve(false);
          return;
        }
        state.modal = null;
        render();
      }
    });
  }
}

function bindFormEvents(form: HTMLFormElement): void {
  const bookId = form.dataset.bookId!;
  const isNew = state.view === "add";

  state.formSnapshot = serializeForm(form);

  // Star rating interactive clicks
  form.querySelectorAll<HTMLElement>(".star-rating__star").forEach((star) => {
    star.addEventListener("click", () => {
      const val = Number(star.dataset.value) as StarRating;
      const ratingContainer = star.parentElement!;
      ratingContainer.querySelectorAll(".star-rating__star").forEach((s, i) => {
        s.classList.toggle("star-rating__star--filled", i < val);
      });
      ratingContainer.dataset.selectedRating = String(val);
    });
  });

  // Date chip selectors
  let calendarCleanup: (() => void) | null = null;

  bindDateChipEvents(form, (fieldName, currentValue) => {
    // Close any existing calendar first
    if (calendarCleanup) {
      calendarCleanup();
      calendarCleanup = null;
    }
    form.querySelectorAll(".date-chips__calendar-anchor").forEach((a) => {
      a.innerHTML = "";
    });

    const chipsContainer = form.querySelector<HTMLElement>(
      `.date-chips[data-field="${fieldName}"]`,
    );
    if (!chipsContainer) return;

    const anchor = chipsContainer.querySelector<HTMLElement>(
      ".date-chips__calendar-anchor",
    );
    if (!anchor) return;

    const calendarHtml = renderCalendar({
      selectedDate: currentValue || undefined,
    });
    anchor.innerHTML = calendarHtml;

    const overlayEl = anchor.querySelector<HTMLElement>(
      ".lcars-calendar-overlay",
    );
    if (!overlayEl) return;

    const cleanup = bindCalendarEvents(overlayEl, {
      onSelect: (dateValue) => {
        updateChipToPickState(chipsContainer, dateValue);
        if (calendarCleanup) {
          calendarCleanup();
          calendarCleanup = null;
        }
        anchor.innerHTML = "";
      },
      onCancel: () => {
        if (calendarCleanup) {
          calendarCleanup();
          calendarCleanup = null;
        }
        anchor.innerHTML = "";
      },
    });
    calendarCleanup = cleanup;
  });

  // Auto-fill date chips when status changes
  form.querySelector("#bf-status")?.addEventListener("change", () => {
    const status = (form.querySelector("#bf-status") as HTMLSelectElement)
      ?.value;
    if (status === "read") {
      const chips = form.querySelector('.date-chips[data-field="dateRead"]');
      const hidden = chips?.querySelector<HTMLInputElement>(
        "input[type=hidden]",
      );
      if (hidden && !hidden.value) {
        chips!
          .querySelector<HTMLButtonElement>(".date-chip--blue")!
          .click();
      }
    }
    if (status === "reading") {
      const chips = form.querySelector(
        '.date-chips[data-field="dateStarted"]',
      );
      const hidden = chips?.querySelector<HTMLInputElement>(
        "input[type=hidden]",
      );
      if (hidden && !hidden.value) {
        chips!
          .querySelector<HTMLButtonElement>(".date-chip--blue")!
          .click();
      }
    }
  });

  // Add recommendation
  form.querySelector("#add-rec-btn")?.addEventListener("click", () => {
    const fromEl = form.querySelector<HTMLInputElement>("#rec-from")!;
    const commentEl = form.querySelector<HTMLInputElement>("#rec-comment")!;
    if (fromEl.value.trim() && commentEl.value.trim()) {
      // Get the current book being edited (or use a temp storage)
      const book = isNew
        ? getFormTempBook(form, bookId)
        : state.library.books.find((b) => b.id === bookId);

      if (book) {
        book.recommendations.push({
          from: fromEl.value.trim(),
          comment: commentEl.value.trim(),
        });
        render();
      }
    }
  });

  // Remove recommendation
  form.querySelectorAll<HTMLButtonElement>(".remove-rec-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      const idx = Number(btn.dataset.recIndex);
      const book = state.library.books.find((b) => b.id === bookId);
      if (book) {
        book.recommendations.splice(idx, 1);
        render();
      }
    });
  });

  // Submit
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const data = new FormData(form);
    const ratingEl = form.querySelector<HTMLElement>(".star-rating");
    const ratingVal = ratingEl?.dataset.selectedRating;

    const bookData: Partial<Book> = {
      title: (data.get("title") as string).trim(),
      author: (data.get("author") as string).trim(),
      datePublished: (data.get("datePublished") as string).trim(),
      isbn: (data.get("isbn") as string).trim() || undefined,
      series: (data.get("series") as string).trim() || undefined,
      seriesNumber: (data.get("seriesNumber") as string).trim() || undefined,
      status: data.get("status") as ReadingStatus,
      rating: ratingVal ? (Number(ratingVal) as StarRating) : undefined,
      notes: (data.get("notes") as string).trim() || undefined,
      coverUrl: (data.get("coverUrl") as string)?.trim() || undefined,
      dateStarted: (data.get("dateStarted") as string) || undefined,
      dateRead: (data.get("dateRead") as string) || undefined,
    };

    if (bookData.status === "read" && !bookData.rating) {
      // If read but no rating, keep as undefined
    }

    const toastMessage = isNew ? "Book added" : "Book updated";

    if (isNew) {
      const newBook = createNewBook({ ...bookData, id: bookId });
      state.library.books.push(newBook);
    } else {
      const existing = state.library.books.find((b) => b.id === bookId);
      if (existing) {
        Object.assign(existing, bookData);
      }
    }

    const saved = await performSave(() => storage.save(state.library));

    if (saved) {
      state.view = "list";
      state.selectedBookId = null;
      state.formSnapshot = null;
      router.navigateTo("/");
      render();
      showToast(toastMessage);
    }
    // If not saved (user cancelled from error), form is still intact underneath
  });

  // -- Shared book-field prepopulation from Open Library result -------------
  function populateFormFromResult(r: OpenLibraryResult): void {
    if (r.title)
      form.querySelector<HTMLInputElement>("#bf-title")!.value = r.title;
    if (r.author)
      form.querySelector<HTMLInputElement>("#bf-author")!.value = r.author;
    if (r.firstPublishYear)
      form.querySelector<HTMLInputElement>("#bf-published")!.value =
        r.firstPublishYear;
    if (r.isbn)
      form.querySelector<HTMLInputElement>("#bf-isbn")!.value = r.isbn;
    if (r.series)
      form.querySelector<HTMLInputElement>("#bf-series")!.value = r.series;
    if (r.seriesNumber)
      form.querySelector<HTMLInputElement>("#bf-series-num")!.value =
        r.seriesNumber;
    const coverInput =
      form.querySelector<HTMLInputElement>("#bf-cover-url")!;
    if (r.coverUrl) coverInput.value = r.coverUrl;
    const preview = document.getElementById("cover-preview");
    if (preview)
      preview.innerHTML = renderCoverImage(
        coverInput.value || undefined,
        "large",
      );
  }

  // -- Title autocomplete ---------------------------------------------------
  const titleInput = form.querySelector<HTMLInputElement>("#bf-title");
  if (titleInput) {
    activeAutocompletes.push(
      new Autocomplete({
        input: titleInput,
        minChars: 3,
        debounceMs: 1000,
        source: async (query) => {
          const results = await searchBooks(query);
          return results.map((r) => ({
            label: r.title,
            sublabel: [r.author, r.firstPublishYear]
              .filter(Boolean)
              .join(" · "),
            value: r,
          }));
        },
        onSelect: (item) => {
          populateFormFromResult(item.value as OpenLibraryResult);
        },
      }),
    );
  }

  // -- ISBN autocomplete ----------------------------------------------------
  const isbnInput = form.querySelector<HTMLInputElement>("#bf-isbn");
  if (isbnInput) {
    activeAutocompletes.push(
      new Autocomplete({
        input: isbnInput,
        minChars: 3,
        debounceMs: 1000,
        source: async (query) => {
          const results = await searchByIsbn(query);
          return results.map((r) => ({
            label: r.title,
            sublabel: [r.isbn, r.author, r.firstPublishYear]
              .filter(Boolean)
              .join(" · "),
            value: r,
          }));
        },
        onSelect: (item) => {
          populateFormFromResult(item.value as OpenLibraryResult);
        },
      }),
    );
  }

  // -- Series autocomplete --------------------------------------------------
  const seriesInput = form.querySelector<HTMLInputElement>("#bf-series");
  if (seriesInput) {
    activeAutocompletes.push(
      new Autocomplete({
        input: seriesInput,
        minChars: 1,
        debounceMs: 0,
        source: (query) => {
          const lower = query.toLowerCase();
          const names = new Set<string>();
          for (const b of state.library.books) {
            if (b.series) names.add(b.series);
          }
          return [...names]
            .filter((n) => n.toLowerCase().includes(lower))
            .sort()
            .map((n) => ({ label: n, value: n }));
        },
        onSelect: (item) => {
          seriesInput.value = item.label;
        },
      }),
    );
  }

  // -- Cover URL live preview -----------------------------------------------
  const coverUrlInput =
    form.querySelector<HTMLInputElement>("#bf-cover-url");
  if (coverUrlInput) {
    coverUrlInput.addEventListener("input", () => {
      const preview = document.getElementById("cover-preview");
      if (preview)
        preview.innerHTML = renderCoverImage(
          coverUrlInput.value.trim() || undefined,
          "large",
        );
    });
  }
}

function destroyAutocompletes(): void {
  for (const ac of activeAutocompletes) ac.destroy();
  activeAutocompletes = [];
}

function serializeForm(form: HTMLFormElement): string {
  const data = new FormData(form);
  const entries: string[] = [];
  data.forEach((value, key) => entries.push(`${key}=${value}`));
  const rating = form.querySelector<HTMLElement>(".star-rating")?.dataset.selectedRating ?? "";
  entries.push(`_rating=${rating}`);
  return entries.join("&");
}

function saveScrollPosition(): void {
  const content = appEl.querySelector<HTMLElement>(".lcars-content");
  if (content) {
    state.savedScrollTop = content.scrollTop;
  }
}

function saveDeepLinkPath(): void {
  if (window.location.pathname !== "/") {
    sessionStorage.setItem(SS_AUTH_REDIRECT_PATH, window.location.pathname);
  }
}

// Temp book storage for adding recommendations to new books
const _tempBooks = new Map<string, Book>();

function getFormTempBook(form: HTMLFormElement, bookId: string): Book {
  if (!_tempBooks.has(bookId)) {
    const data = new FormData(form);
    _tempBooks.set(
      bookId,
      createNewBook({
        id: bookId,
        title: (data.get("title") as string) || "",
        author: (data.get("author") as string) || "",
      }),
    );
  }
  return _tempBooks.get(bookId)!;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

function showToast(message: string): void {
  state.toast = message;
  render();
  setTimeout(() => {
    state.toast = null;
    render();
  }, 3000);
}

function handleSignOut(): void {
  storage.signOut();
  showToast("Signed out");
  // After sign-out, show auth-required screen
  renderAuthRequired();
}

function handleExport(): void {
  storage.exportLibraryToFile(state.library);
  showToast("Library exported");
}

async function handleImport(): Promise<void> {
  try {
    const lib = await storage.importLibraryFromFile();
    state.library = lib;
    await storage.save(state.library);
    showToast(`Imported ${lib.books.length} books`);
    render();
  } catch (err) {
    console.error("Import failed:", err);
    showToast("Import failed");
  }
}

async function handleFindMissingCovers(): Promise<void> {
  const missing = state.library.books.filter((b) => !b.coverUrl);
  if (missing.length === 0) {
    showToast("All books already have covers!");
    return;
  }
  await runCoverScan(state.library, () => render());
}

async function handleGoodreadsImport(): Promise<void> {
  try {
    const lib = await importGoodreadsFile();
    // Merge: add imported books to existing library
    const existingTitles = new Set(
      state.library.books.map((b) => `${b.title}::${b.author}`.toLowerCase()),
    );
    let added = 0;
    for (const book of lib.books) {
      const key = `${book.title}::${book.author}`.toLowerCase();
      if (!existingTitles.has(key)) {
        state.library.books.push(book);
        existingTitles.add(key);
        added++;
      }
    }
    await storage.save(state.library);
    showToast(
      `Imported ${added} books from Goodreads (${lib.books.length - added} duplicates skipped)`,
    );
    render();
  } catch (err) {
    console.error("Goodreads import failed:", err);
    showToast("Goodreads import failed");
  }
}
